import type { WhatsAppSettings } from '@orion/contracts';
import { PageHeader } from '../../../app/shell.js';
import { useSettingsQuery, useUpdateSettings } from '../settings/api.js';

/** Admin ▸ Integrations ▸ WhatsApp. No provider/credential fields here —
 * same reasoning as Calling & meetings' Twilio card: the Meta Cloud API
 * access token/app secret are env vars/Doppler
 * (`apps/api/src/config/env.ts`'s `WHATSAPP_*`), set once by whoever
 * provisions the workspace. `enabled` is a tenant-level kill switch
 * independent of those — it can pause webhook processing without
 * pulling credentials. */
export function WhatsAppSettingsPage() {
  const { data, isLoading } = useSettingsQuery<WhatsAppSettings>('whatsapp');
  const update = useUpdateSettings<WhatsAppSettings>('whatsapp');

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Admin' }, { label: 'WhatsApp' }]} />
      <main className="flex flex-1 flex-col gap-4 overflow-y-auto px-7 py-5">
        {!isLoading && data && (
          <section aria-labelledby="wa-h" className="flex max-w-xl flex-col gap-4 rounded-dialog border border-border bg-surface p-4.5 shadow-card">
            <div className="flex items-center justify-between">
              <h2 id="wa-h" className="text-[15px] font-semibold">
                WhatsApp Business
              </h2>
              <label className="flex items-center gap-2 text-[13px] font-semibold">
                Enabled
                <input
                  type="checkbox"
                  checked={data.enabled}
                  onChange={(e) => update.mutate({ ...data, enabled: e.target.checked })}
                  style={{ accentColor: 'var(--color-primary)' }}
                />
              </label>
            </div>
            <p className="text-xs text-text-muted">
              Connects this workspace's WhatsApp Business number (via the official Meta Cloud API) to the CRM — incoming messages become
              conversations in the WhatsApp Inbox, automatically matched to an existing Lead/Contact/Company by phone number, or qualified
              by AI the same way Inbox Pulse qualifies email replies (see Admin ▸ AI lead qualification for those thresholds — they apply
              to WhatsApp too). Requires{' '}
              <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">WHATSAPP_ACCESS_TOKEN</code>,{' '}
              <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">WHATSAPP_APP_SECRET</code>, and{' '}
              <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">WHATSAPP_WEBHOOK_VERIFY_TOKEN</code> to be set for
              this workspace via Doppler/environment configuration, and a webhook subscribed in Meta's App Dashboard pointing at this
              server's <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">/api/v1/whatsapp/webhook</code>.
            </p>
          </section>
        )}
      </main>
    </>
  );
}
